<template>
  <v-container fluid class="pa-0 company-users-list-container">
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-data-table
            class="admin-list-table"
            :headers="headers"
            :items="companyUsers"
            :search="search"
            :sort-by="['role']"
            :loading="loading"
            @click:row="redirectToSummary"
          >
            <template #top>
              <v-container fluid>
                <v-row>
                  <v-col cols="12" md="4" class="pa-md-6">
                    <v-text-field
                      v-model="search"
                      :append-icon="icons.mdiMagnify"
                      label="Search"
                      single-line
                      hide-details
                    />
                  </v-col>

                  <v-col cols="12" md="4" class="pa-md-6">
                    <!-- Filter for roles -->
                    <v-select
                      v-model="roleFilterValue"
                      :items="rolesList"
                      label="Roles"
                    />
                  </v-col>

                  <v-col cols="12" md="4" class="pa-md-6">
                    <v-menu
                      v-model="dateMenu"
                      min-width="290px"
                      :close-on-content-click="false"
                    >
                      <template #activator="{ on }">
                        <v-row no-gutters>
                          <v-col cols="9">
                            <v-text-field
                              v-model="dateRangeText"
                              label="Date range"
                              :prepend-icon="icons.mdiCalendar"
                              readonly
                              v-on="on"
                            />
                          </v-col>
                          <v-col cols="3">
                            <v-btn depressed @click="clearDates()">
                              Clear dates
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>
                      <v-date-picker
                        v-model="dateRange"
                        range
                        @input="closeMenu()"
                      />
                    </v-menu>
                  </v-col>
                </v-row>
              </v-container>
            </template>

            <template #[`item.role`]="{ item }">
              <v-chip :color="roleDetails(item.role).color" dark>{{
                roleDetails(item.role).text
              }}</v-chip>
            </template>

            <template #[`item.acceptedTerms`]="{ item }">
              <v-icon
                :color="item.acceptedTerms ? 'primary' : 'mid-grey'"
                dark
                >{{
                  item.acceptedTerms
                    ? icons.mdiAccountCheckOutline
                    : icons.mdiAccountClockOutline
                }}</v-icon
              >
            </template>

            <template #[`item.searchAdvisor`]="{ item }">
              <div
                class="search-advisor"
                @click.stop="searchAdvisor(item.email)"
              >
                <v-icon color="dark-grey" class="search-advisor-icon">{{
                  icons.mdiAccountSearchOutline
                }}</v-icon>
              </div>
            </template>
          </v-data-table>

          <v-btn depressed @click="csvDownload()">
            <v-icon small>{{ icons.mdiDownload }}</v-icon
            >Download csv
          </v-btn>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import { COMPANY_USERS_QUERY } from '../../../constants/graphql/companies';
  import moment from 'moment';
  import { csvExport } from '../../../constants/js/csv';

  export default {
    name: 'CompanyUsersList',
    props: {
      company: {
        required: true,
        type: Object,
      },
      includesTestators: {
        default: true,
        type: Boolean,
      },
      showSearchAdvisor: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        roleFilterValue: null,
        search: '',
        companyUsers: [],
        dates: [],
        dateMenu: false,
        loading: true,
      };
    },
    computed: {
      headers() {
        return this.showSearchAdvisor
          ? this.allHeaders
          : this.allHeaders.filter(h => h.value != 'searchAdvisor');
      },
      allHeaders() {
        return [
          { text: 'Full name', value: 'fullName' },
          { text: 'Email', value: 'email' },
          { text: 'Role', value: 'role', filter: this.roleFilter },
          { text: 'Account activated', value: 'acceptedTerms' },
          { text: 'Registrations', value: 'numberOfRegistrations' },
          { text: 'Testators', value: 'testatorCount' },
          { text: 'Wills', value: 'willCount' },
          { text: 'Completed Wills', value: 'numberOfWillsCompleted' },
          { text: 'Signed wills', value: 'validUploadedWillCount' },
          { text: 'View testators', value: 'searchAdvisor' },
        ];
      },
      rolesList() {
        var list = [
          { text: 'All', value: null },
          { text: 'Company admins', value: 'COMPANY_ADMIN' },
          { text: 'Advisors', value: 'ADVISOR' },
        ];

        if (this.company.companyType == 'AUTO_ASSIGN') {
          list.push({
            text: 'Fiduciary consultants',
            value: 'FIDUCIARY_CONSULTANT',
          });
        }
        if (this.includesTestators) {
          list.push({ text: 'Testators', value: 'TESTATOR' });
        }

        return list;
      },
      dateRange: {
        get: function () {
          return this.dates;
        },
        set: function (newValue) {
          // ensure that the date range is in the correct order
          if (
            newValue.length == 2 &&
            moment(newValue[1]).isBefore(newValue[0], 'day')
          ) {
            this.dates = [newValue[1], newValue[0]];
          } else {
            this.dates = newValue;
          }
        },
      },
      dateRangeText() {
        var formattedDates = [];
        for (const date of this.dates) {
          formattedDates.push(moment(date, 'YYYY-MM-DD').format('D MMM YY'));
        }
        return formattedDates.join(' - ');
      },
      csvData() {
        return this.companyUsers.map(user => ({
          'Full name': user.fullName,
          Email: user.email,
          Role: this.roleDetails(user.role).text,
          'Account activated': user.acceptedTerms,
          'Testators created': user.testatorCount,
          'Unique testator wills created': user.willCount,
          'Valid signed wills uploaded': user.validUploadedWillCount,
        }));
      },
      csvFileName() {
        var fileName = `${this.company.tradingName} user list. `;
        fileName += this.dates.length
          ? this.dateRangeText
          : `All data as of ${moment().format('D MMM YYYY')}`;
        fileName += '.csv';
        return fileName;
      },
    },
    methods: {
      roleDetails(role) {
        switch (role) {
          case 'TESTATOR':
            return {
              text: 'Testator',
              color: 'green',
            };
          case 'COMPANY_ADMIN':
            return {
              text: 'Company admin',
              color: 'purple',
            };
          case 'ADVISOR':
            return {
              text: 'Advisor',
              color: 'orange',
            };
          case 'FIDUCIARY_CONSULTANT':
            return {
              text: 'Fiduciary consultant',
              color: 'brown',
            };
          case 'CALL_CENTER_AGENT':
            return {
              text: 'Call center agent',
              color: 'blue',
            };
          case 'BRANCH_AGENT':
            return {
              text: 'Branch agent',
              color: 'pink',
            };
          default:
            return {
              text: role,
              color: '',
            };
        }
      },
      roleFilter(value) {
        // If this filter has no value we just skip the entire filter.
        if (!this.roleFilterValue) {
          return true;
        }
        // Check if the current loop value (The role value)
        // equals to the selected value at the <v-select>.
        return value === this.roleFilterValue;
      },
      searchAdvisor(email) {
        this.$emit('search-advisor', email);
      },
      redirectToSummary(item) {
        if (item.testatorId) {
          this.$router.push({
            name: 'WillSummary',
            params: {
              id: item.testatorId,
              referrerRoute: this.$route.name,
              referrerId: this.$route.params.id,
            },
          });
        }
      },
      closeMenu() {
        if (this.dates.length == 2) {
          this.dateMenu = false;
        }
      },
      clearDates() {
        this.dates = [];
      },
      csvDownload() {
        csvExport(this.csvData, this.csvFileName);
      },
    },
    apollo: {
      companyUsers: {
        query: COMPANY_USERS_QUERY,
        variables() {
          return {
            id: this.company.id,
            includeTestators: this.includesTestators,
            dateRange: this.dates,
          };
        },
        fetchPolicy: 'cache-and-network',
        skip() {
          return !this.company.id;
        },
        result(response) {
          if (response.data) this.loading = false;
        },
      },
    },
  };
</script>
